import { useEffect, useMemo, useState } from 'react';

import { addUFOCustomCohortData } from '@atlaskit/react-ufo/add-ufo-custom-cohort-data';

import type { SearchStoreExperience } from '../../../../common/constants/search';
import { SearchExperience } from '../../../../common/constants/search-experience';
import { isQueryBootstrapConfigSnapshotExperimentEnabled } from '../../../../common/utils/experiments';
import type { UseFetchConfigReturn } from '../../../config';
import {
	completeQueryBootstrapSnapshotTransition,
	createQueryBootstrapSnapshotIdentity,
	isQueryBootstrapSnapshotValidForIdentity,
	publishQueryBootstrapSnapshot,
	readPreparedQueryBootstrapSnapshot,
} from '../../../config/query-bootstrap-snapshot-store';

type UseQueryBootstrapConfigSnapshotArgs = {
	experience: SearchStoreExperience;
	cloudId: string | undefined;
	fetchedConfig: UseFetchConfigReturn;
};

/**
 * Hands a complete Quick Find config snapshot to the next matching Search Page mount.
 * The route's normal config lifecycle remains active and takes ownership once it resolves.
 */
export const useQueryBootstrapConfigSnapshot = ({
	experience,
	cloudId,
	fetchedConfig,
}: UseQueryBootstrapConfigSnapshotArgs): UseFetchConfigReturn => {
	const enabled = isQueryBootstrapConfigSnapshotExperimentEnabled();
	const identity = useMemo(() => createQueryBootstrapSnapshotIdentity({ cloudId }), [cloudId]);
	const [routeTransition] = useState(() => {
		if (
			!enabled ||
			experience !== SearchExperience.SEARCH_PAGE ||
			typeof window === 'undefined' ||
			!identity
		) {
			return null;
		}

		return readPreparedQueryBootstrapSnapshot(identity);
	});
	const routeSnapshot = routeTransition?.snapshot ?? null;
	// Expiry controls whether a route may adopt a snapshot. Once adopted, retain that exact
	// snapshot until the route's normal config lifecycle takes ownership. Rechecking Date.now()
	// on every render could otherwise revoke bootstrap state midway through a slow route load.
	const validRouteSnapshot = useMemo(
		() =>
			routeSnapshot && identity && isQueryBootstrapSnapshotValidForIdentity(routeSnapshot, identity)
				? routeSnapshot
				: null,
		[identity, routeSnapshot],
	);

	useEffect(() => {
		if (routeTransition && validRouteSnapshot) {
			completeQueryBootstrapSnapshotTransition(routeTransition);
		}
	}, [routeTransition, validRouteSnapshot]);

	useEffect(() => {
		if (
			!enabled ||
			experience !== SearchExperience.QUICK_FIND ||
			typeof window === 'undefined' ||
			!identity ||
			!fetchedConfig.isConfigLoaded
		) {
			return;
		}

		publishQueryBootstrapSnapshot({ identity, config: fetchedConfig });
	}, [enabled, experience, identity, fetchedConfig]);

	useEffect(() => {
		if (enabled && experience === SearchExperience.SEARCH_PAGE) {
			addUFOCustomCohortData(
				'queryBootstrapSnapshotState',
				validRouteSnapshot === null ? 'miss' : 'hit',
			);
		}
	}, [enabled, experience, validRouteSnapshot]);

	if (validRouteSnapshot && !fetchedConfig.isConfigLoaded) {
		return {
			...validRouteSnapshot.config,
			onOAuthSuccess: fetchedConfig.onOAuthSuccess,
		};
	}

	return fetchedConfig;
};
